<script setup lang="ts">
import { computed, useAttrs } from 'vue'
import { RouterLink } from 'vue-router'
import Button from 'primevue/button'
import type { RouteLocationRaw } from 'vue-router'
import AppIcon from '@/components/ui/AppIcon.vue'

interface Props {
  variant?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warn'
    | 'danger'
    | 'ghost'
    | 'outline'
    | 'inverse'
    | 'glass'
    | 'gradient'
    | 'bare'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  loading?: boolean
  disabled?: boolean
  fullWidth?: boolean
  iconOnly?: boolean
  as?: 'button' | 'a' | 'router-link'
  to?: RouteLocationRaw
  href?: string
}

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  loading: false,
  disabled: false,
  fullWidth: false,
  iconOnly: false,
  as: undefined,
  to: undefined,
  href: undefined,
})

const attrs = useAttrs()

// Extract class from attrs to merge with our classes
const externalClass = computed(() => (attrs.class as string) || '')

// Get attrs without class (to avoid duplication)
const attrsWithoutClass = computed(() => {
  const { class: _, ...rest } = attrs
  return rest
})

const variantClasses: Record<NonNullable<Props['variant']>, string> = {
  primary:
    'tw-bg-primary-600 hover:tw-bg-primary-700 active:tw-bg-primary-800 tw-text-white tw-border-transparent tw-shadow-sm hover:tw-shadow-md dark:tw-bg-primary-600 dark:hover:tw-bg-primary-700 dark:active:tw-bg-primary-800',
  // green-700 and amber-700, not the success and warning tokens: white text on
  // #22c55e is 2.28:1 and on #f59e0b 2.15:1, both unreadable.
  success:
    'tw-bg-green-700 hover:tw-bg-green-800 active:tw-bg-green-900 tw-text-white tw-border-transparent tw-shadow-sm hover:tw-shadow-md',
  warn:
    'tw-bg-amber-700 hover:tw-bg-amber-800 active:tw-bg-amber-900 tw-text-white tw-border-transparent tw-shadow-sm hover:tw-shadow-md',
  secondary:
    'tw-bg-surface-100 hover:tw-bg-surface-200 active:tw-bg-surface-300 tw-text-surface-700 tw-border-surface-300',
  danger:
    'tw-bg-error-dark hover:tw-bg-red-700 active:tw-bg-red-800 tw-text-white tw-border-transparent tw-shadow-sm hover:tw-shadow-md',
  ghost:
    'tw-bg-transparent hover:tw-bg-surface-100 active:tw-bg-surface-200 tw-text-surface-700 tw-border-transparent',
  outline:
    'tw-bg-transparent hover:tw-bg-primary-50 active:tw-bg-primary-100 tw-text-primary-600 tw-border-primary-600 hover:tw-border-primary-700 dark:hover:tw-bg-primary-900/30 dark:active:tw-bg-primary-900/50 dark:tw-text-primary-400 dark:tw-border-primary-400',
  inverse:
    'tw-bg-white hover:tw-bg-surface-50 active:tw-bg-surface-100 tw-text-primary-700 tw-border-transparent tw-shadow-lg hover:tw-shadow-xl',
  glass:
    'tw-bg-white/20 hover:tw-bg-white/30 tw-backdrop-blur-sm tw-text-white tw-border tw-border-white/50 hover:tw-border-white/60',
  gradient:
    'tw-bg-primary-600 tw-text-white tw-border-transparent tw-shadow-lg tw-shadow-primary-500/30 hover:tw-shadow-xl hover:tw-shadow-primary-500/40 tw-relative tw-overflow-hidden',
  // Carries no chrome of its own: for row-shaped controls (navigation items,
  // list rows) that must be a <button> for keyboard and screen reader users but
  // own their whole layout. The base chrome cannot be overridden from the call
  // site, because Tailwind decides between two utilities by stylesheet order,
  // not by the order they are passed in.
  bare: '',
}

const sizeClasses: Record<NonNullable<Props['size']>, string> = {
  sm: 'tw-text-sm tw-px-3 tw-py-1.5 tw-gap-1.5',
  md: 'tw-text-base tw-px-4 tw-py-2 tw-gap-2',
  lg: 'tw-text-lg tw-px-6 tw-py-3 tw-gap-2.5',
  xl: 'tw-text-lg tw-px-8 tw-py-4 tw-gap-2.5 tw-font-semibold',
}

const iconOnlySizeClasses: Record<NonNullable<Props['size']>, string> = {
  sm: 'tw-w-8 tw-h-8 tw-p-0',
  md: 'tw-w-10 tw-h-10 tw-p-0',
  lg: 'tw-w-12 tw-h-12 tw-p-0',
  xl: 'tw-w-14 tw-h-14 tw-p-0',
}

// Computed for polymorphic component type
const componentType = computed(() => {
  if (props.as === 'router-link' || props.to) return RouterLink
  if (props.as === 'a' || props.href) return 'a'
  return Button
})

const isButton = computed(() => componentType.value === Button)

// All classes combined for the component
const isBare = computed(() => props.variant === 'bare')

const buttonClasses = computed(() => [
  isBare.value
    ? 'tw-cursor-pointer'
    : 'tw-inline-flex tw-items-center tw-justify-center tw-font-medium tw-rounded-lg tw-border tw-cursor-pointer',
  isBare.value ? '' : 'tw-transition-all tw-duration-200 tw-ease-out',
  isBare.value
    ? ''
    : 'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-500 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-offset-surface-0',
  variantClasses[props.variant],
  isBare.value ? '' : props.iconOnly ? iconOnlySizeClasses[props.size] : sizeClasses[props.size],
  {
    'tw-opacity-50 tw-cursor-not-allowed tw-pointer-events-none': props.disabled || props.loading,
    'tw-w-full': props.fullWidth,
  },
  externalClass.value,
])

// PrimeVue pass-through for Button component
const buttonPt = computed(() => ({
  root: {
    class: buttonClasses.value,
  },
}))
</script>

<template>
  <!-- PrimeVue Button - uses pt prop for styling -->
  <Button
    v-if="isButton"
    :pt="buttonPt"
    :disabled="props.disabled || props.loading"
    :aria-disabled="props.disabled || props.loading"
    :aria-busy="props.loading"
    unstyled
    v-bind="attrsWithoutClass"
  >
    <div
      v-if="props.variant === 'gradient'"
      class="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-from-primary-600 tw-to-blue-700 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity"
    ></div>
    <span :class="{ 'tw-relative tw-z-10 tw-flex tw-items-center tw-gap-2': props.variant === 'gradient' }">
      <AppIcon
        v-if="props.loading"
        name="solar:spinner-bold"
        size="md"
        spin
        :class="{ 'tw-mr-2': !props.iconOnly }"
      />
      <slot />
    </span>
  </Button>

  <!-- RouterLink or anchor - uses class binding -->
  <component
    v-else
    :is="componentType"
    :to="props.to"
    :href="props.href"
    :class="buttonClasses"
    :aria-disabled="props.disabled || props.loading"
    :aria-busy="props.loading"
    v-bind="attrsWithoutClass"
  >
    <div
      v-if="props.variant === 'gradient'"
      class="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-from-primary-600 tw-to-blue-700 tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity"
    ></div>
    <span :class="{ 'tw-relative tw-z-10 tw-flex tw-items-center tw-gap-2': props.variant === 'gradient' }">
      <AppIcon
        v-if="props.loading"
        name="solar:spinner-bold"
        size="md"
        spin
        :class="{ 'tw-mr-2': !props.iconOnly }"
      />
      <slot />
    </span>
  </component>
</template>
